/**
 * **La file de capture hors ligne appartient à un utilisateur** (ticket 01 du chantier comptes).
 *
 * Plusieurs personnes peuvent se connecter sur le même téléphone. Une capture faite par A et
 * rejouée pendant que B est connecté partirait chez B, avec le cookie de B : le serveur ne peut
 * pas le voir, il ne lit l'utilisateur que dans la session. C'est donc le client qui garde la
 * frontière : chaque capture porte l'utilisateur qui l'a faite, et seules les siennes partent.
 *
 * Règles pures, sans IndexedDB ni réseau (testées dans tests/client/).
 */

export interface CaptureRangee {
  cle: string;
  poseeLe: number;
  /** Qui l'a capturée. Absent sur une capture d'avant les comptes. */
  utilisateur?: string;
}

/**
 * Les captures de l'utilisateur connecté, dans l'ordre où il y a pensé, et celles à jeter.
 *
 * Celles d'un autre se jettent : les garder laisserait les titres de A en clair sur le
 * téléphone pendant que B s'en sert, et c'est ce que la déconnexion évite déjà en vidant la
 * file. Celles d'avant les comptes, sans propriétaire, se jettent aussi : les adopter, c'est
 * les donner au premier qui se connecte.
 *
 * Personne de connecté : on ne sait à qui rien n'appartient, on ne touche à rien.
 */
export function partagerLaFile<T extends CaptureRangee>(
  file: T[],
  utilisateur: string | null
): { aMoi: T[]; aJeter: T[] } {
  if (utilisateur === null) return { aMoi: [], aJeter: [] };
  const aMoi = file.filter((c) => c.utilisateur === utilisateur).sort((a, b) => a.poseeLe - b.poseeLe);
  const aJeter = file.filter((c) => c.utilisateur !== utilisateur);
  return { aMoi, aJeter };
}

/**
 * Un échec qui arrête la file sans jeter la capture : le réseau absent, ou la session tombée
 * en route. La capture repartira quand son utilisateur sera de nouveau connecté. Tout autre
 * refus (un parent supprimé entre-temps) la retire, sinon elle bloquerait la file pour toujours.
 */
export function arreteLaFile(code: string): boolean {
  return code === 'hors_ligne' || code === 'session_absente';
}
